import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import Save from "@mui/icons-material/Save";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { ReviewerSignerPicker } from "./ReviewerSignerPicker";
import { SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { SignatureType } from "generated-graphql/graphql";
import { useReviewerIssues } from "contexts/SubmissionFormContext";

interface WetInkSignerDialogProps {
  initialSigner: SubmissionReviewerSigner | null;
  signatureType: SignatureType;
  open: boolean;
  onClose: () => void;
  onSave: (signer: SubmissionReviewerSigner) => void;
  onChange?: (signer: SubmissionReviewerSigner | null) => void;
}

export function SignerDialog({
  open,
  onClose,
  onSave,
  onChange,
  initialSigner,
  signatureType,
}: WetInkSignerDialogProps) {
  // Store initial snapshot for cancel/revert
  const initialSnapshotRef = useRef(initialSigner);

  const [signer, setSigner] = useState<SubmissionReviewerSigner | null>(
    initialSigner
  );
  const [signerAddress, setSignerAddress] = useState(
    initialSigner?.address ?? ""
  );
  const [dueDate, setDueDate] = useState<DateTime | null>(
    initialSigner?.dueDate ?? null
  );

  const issues = useReviewerIssues();
  const signerIssues = useMemo(
    () =>
      signer?.id
        ? issues.filter((i) => i.schemaStack?.some((s) => s.name === signer.id))
        : [],
    [issues, signer?.id]
  );

  const dueDateIssues = useMemo(
    () => signerIssues.filter((i) => i.key === "dueDate"),
    [signerIssues]
  );

  // Notify parent when changes happen - using ref to avoid adding to deps
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!onChangeRef.current) return;

    if (signer?.id) {
      const updatedSigner = {
        ...signer,
        address: signerAddress,
        dueDate: dueDate || undefined,
        isSigner: true,
      };
      onChangeRef.current(updatedSigner);
    } else {
      onChangeRef.current(null);
    }
  }, [signer, signerAddress, dueDate]);

  const handleCancel = () => {
    // Revert to initial snapshot
    if (onChange) {
      onChange(initialSnapshotRef.current);
    }
    onClose();
  };

  const handleSave = () => {
    if (
      signer?.id &&
      (signatureType === SignatureType.WetInk ? signerAddress.trim() : true)
    ) {
      onSave({
        ...signer,
        address: signerAddress,
        dueDate: dueDate || undefined,
        isSigner: true,
      });
    }
  };

  const canSave =
    signer?.id &&
    (signatureType === SignatureType.WetInk ? signerAddress.trim() : true);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" component="div">
              Add {prettyPrintSignatureType(signatureType)} Signer
            </Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Stack spacing={3}>
            {/* Description */}
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ lineHeight: 1.66 }}
            >
              Add a {prettyPrintSignatureType(signatureType)} signer to the
              submission. This person may not also be a reviewer.
            </Typography>

            {/* Form Fields */}
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <ReviewerSignerPicker
                  mode="signer"
                  onChange={(newSigner) => {
                    if (!newSigner) {
                      setSigner(null);
                      return;
                    }
                    setSigner({ ...signer, ...newSigner });
                  }}
                  value={signer}
                />

                <Box>
                  <DatePicker
                    label="Due date"
                    value={dueDate}
                    onChange={(newValue) => setDueDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: dueDateIssues.length > 0,
                      },
                    }}
                  />
                  {dueDateIssues.length > 0 && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{
                        px: 1.5,
                        pt: 0.5,
                        display: "block",
                      }}
                    >
                      {dueDateIssues[0].message}
                    </Typography>
                  )}
                </Box>
              </Stack>
              {signatureType === SignatureType.WetInk && (
                <TextField
                  label="Address *"
                  value={signerAddress}
                  onChange={(e) => setSignerAddress(e.target.value)}
                  fullWidth
                />
              )}
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancel} variant="text">
            CANCEL
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!canSave}
            startIcon={<Save />}
          >
            SAVE
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

function prettyPrintSignatureType(signatureType: SignatureType) {
  switch (signatureType) {
    case SignatureType.Digital:
      return "Digital";
    case SignatureType.WetInk:
      return "Wet-Ink";
  }

  return "";
}
